import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Star, ExternalLink, MousePointerClick } from "lucide-react";
import api from "@/lib/api";

const EMPTY = {
  name: "", slug: "", logo_url: "", tracking_url: "", blurb: "", regions: "",
  min_deposit: "", spread_note: "", platforms: "MT4 / MT5", model_note: "",
  recommended: false, active: true, sort_order: 0,
};

export default function AdminBrokers() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [editing, setEditing] = useState(null);
  const [stats, setStats] = useState({ total: 0, by_broker: {}, by_source: [] });

  const load = () => {
    api.get("/admin/brokers").then((r) => setRows(r.data)).catch(() => {});
    api.get("/admin/brokers/clicks").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const payload = (f) => ({ ...f, sort_order: Number(f.sort_order) || 0, slug: f.slug || null });

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/brokers/${editing}`, payload(form));
        toast.success("Broker updated");
      } else {
        await api.post("/admin/brokers", payload(form));
        toast.success("Broker added");
      }
      setForm({ ...EMPTY }); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const edit = (b) => { setEditing(b.id); setForm({ ...EMPTY, ...b, slug: b.slug || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { if (!window.confirm("Delete this broker?")) return; await api.delete(`/admin/brokers/${id}`); load(); };

  const inp = "w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none";

  return (
    <div data-testid="admin-brokers">
      <div className="mb-6">
        <p className="overline mb-2">Brokers</p>
        <h1 className="font-editorial text-3xl text-[color:var(--ink)]">Broker affiliate partners</h1>
        <p className="mt-2 max-w-2xl text-sm text-[color:var(--ink-body)]">
          Add each broker's <strong>affiliate / IB tracking link</strong>. It stays server-side — the storefront links through
          a redirect that logs every click here. Total clicks tracked: <strong>{stats.total}</strong>.
        </p>
      </div>

      <form onSubmit={submit} className="mb-8 grid gap-3 rounded-xl border border-[color:var(--ink-hairline)] bg-white p-5 md:grid-cols-2" data-testid="broker-form">
        <label className="text-xs text-[color:var(--ink-muted)]">Name
          <input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="broker-name" required />
        </label>
        <label className="text-xs text-[color:var(--ink-muted)]">Tracking / affiliate URL
          <input className={inp} value={form.tracking_url} onChange={(e) => setForm({ ...form, tracking_url: e.target.value })} placeholder="https://partner.broker.com/?ref=YOURID" data-testid="broker-tracking-url" required />
        </label>
        <label className="text-xs text-[color:var(--ink-muted)]">Logo URL (optional)
          <input className={inp} value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} data-testid="broker-logo-url" />
        </label>
        <label className="text-xs text-[color:var(--ink-muted)]">Slug (optional)
          <input className={inp} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto from name" />
        </label>
        <label className="md:col-span-2 text-xs text-[color:var(--ink-muted)]">Blurb
          <textarea className={inp} rows={2} value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} data-testid="broker-blurb" />
        </label>
        <label className="text-xs text-[color:var(--ink-muted)]">Regions
          <input className={inp} value={form.regions} onChange={(e) => setForm({ ...form, regions: e.target.value })} placeholder="Global (ex-US)" />
        </label>
        <label className="text-xs text-[color:var(--ink-muted)]">Min deposit
          <input className={inp} value={form.min_deposit} onChange={(e) => setForm({ ...form, min_deposit: e.target.value })} placeholder="$100" />
        </label>
        <label className="text-xs text-[color:var(--ink-muted)]">Spreads note
          <input className={inp} value={form.spread_note} onChange={(e) => setForm({ ...form, spread_note: e.target.value })} placeholder="Raw spread from 0.0" />
        </label>
        <label className="text-xs text-[color:var(--ink-muted)]">Platforms
          <input className={inp} value={form.platforms} onChange={(e) => setForm({ ...form, platforms: e.target.value })} />
        </label>
        <label className="text-xs text-[color:var(--ink-muted)]">Commission model note
          <input className={inp} value={form.model_note} onChange={(e) => setForm({ ...form, model_note: e.target.value })} placeholder="IB / CPA" />
        </label>
        <label className="text-xs text-[color:var(--ink-muted)]">Sort order
          <input type="number" className={inp} value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
        </label>
        <div className="flex items-center gap-6 md:col-span-2">
          <label className="flex items-center gap-2 text-sm text-[color:var(--ink)]"><input type="checkbox" checked={form.recommended} onChange={(e) => setForm({ ...form, recommended: e.target.checked })} data-testid="broker-recommended" /> Recommended</label>
          <label className="flex items-center gap-2 text-sm text-[color:var(--ink)]"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="broker-active" /> Active</label>
          <button type="submit" className="btn-edge ml-auto text-sm" data-testid="broker-submit"><Plus size={15} /> {editing ? "Save changes" : "Add broker"}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm({ ...EMPTY }); }} className="btn-edge-ghost text-sm">Cancel</button>}
        </div>
      </form>

      <div className="grid gap-3">
        {rows.map((b) => (
          <div key={b.id} data-testid={`admin-broker-${b.slug}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--ink-hairline)] bg-white p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-editorial text-lg text-[color:var(--ink)]">{b.name}</span>
                {b.recommended && <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--evergreen)] px-2 py-0.5 text-[10px] font-bold text-white"><Star size={10} /> Rec</span>}
                {!b.active && <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-bold text-gray-600">Hidden</span>}
              </div>
              <p className="mt-1 truncate text-xs text-[color:var(--ink-muted)]">{b.regions} · {b.model_note} · {b.min_deposit}</p>
              <a href={b.tracking_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] text-[color:var(--evergreen)] hover:underline">{b.tracking_url} <ExternalLink size={11} /></a>
            </div>
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center gap-1.5 text-sm text-[color:var(--ink-body)]" title="Clicks tracked"><MousePointerClick size={14} /> {stats.by_broker?.[b.slug] || 0}</span>
              <button onClick={() => edit(b)} className="btn-edge-ghost text-xs" data-testid={`broker-edit-${b.slug}`}>Edit</button>
              <button onClick={() => remove(b.id)} className="text-red-500 hover:text-red-600" data-testid={`broker-delete-${b.slug}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {stats.by_source?.length > 0 && (
        <div className="mt-8 rounded-xl border border-[color:var(--ink-hairline)] bg-white p-5">
          <p className="overline mb-3">Clicks by source</p>
          <div className="grid gap-1 text-sm">
            {stats.by_source.map((s) => (
              <div key={s.src} className="flex justify-between border-b border-[color:var(--ink-hairline)] py-1.5 text-[color:var(--ink-body)]">
                <span className="font-mono-edge text-xs">{s.src}</span><span className="font-medium text-[color:var(--ink)]">{s.clicks}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
